/**
 * Routes SSOT — the one map that defines every top-level tab.
 *
 * Consumers:
 *   - Sidebar (label, icon, role visibility)
 *   - App.tsx router (which module to mount per tab)
 *   - TopBar breadcrumb (label resolution)
 *   - Future RoleGuardedRoute (access gating)
 *
 * Why an enum-like map instead of React Router? React Router would
 * force URL-driven navigation which our app doesn't use yet; this
 * keeps the state-machine router but gives us a typed registry.
 * Phase H+ can swap the router without touching any consumer.
 *
 * Rules encoded here (plan Phase F):
 *   - `roles: 'any'` → visible to everyone (including unauthenticated
 *     paths that gate at AuthGate level)
 *   - `roles: Role[]` → hidden unless the user has one of the listed
 *     roles. Drives pre-flight gating in App.tsx's router
 *   - `access: 'education' | 'networking'` → additional paywall gate;
 *     the sidebar shows a lock icon when the user lacks the access
 *     flag. Hitting the tab still opens it (the module's own
 *     PaywallGate handles the locked state)
 */

import type { ComponentType } from 'react';
import {
    Bot,
    BookOpen,
    Home,
    MessageSquare,
    Settings,
    User,
    UserCheck,
    Users,
} from 'lucide-react';
import type { BackendUserWithPlan } from '../../userService';
import { isAdmin, isMentee, isMentor } from '../../userService';

export type TabKey =
    | 'home'
    | 'assistant'
    | 'education'
    | 'mentorship'
    | 'networking'
    | 'conversations'
    | 'settings'
    | 'profile';

export type Role = 'admin' | 'mentor' | 'mentee';

export type AccessFlag = 'education' | 'networking';

export type RouteDef = {
    /** Stable key used as the router discriminator + analytics event. */
    key: TabKey;
    /** Default sidebar label. Role-aware overrides live in
     *  `resolveRouteLabel` below so consumers get the right copy. */
    label: string;
    /** Lucide icon component rendered in the sidebar nav row. */
    icon: ComponentType<{ size?: number | string; className?: string }>;
    /** `'any'` = visible to every signed-in user. Otherwise, one of
     *  the roles must match. */
    roles: 'any' | Role[];
    /** Optional access-flag paywall. `education` / `networking`
     *  check BackendUserWithPlan.access for the feature. */
    access?: AccessFlag;
    /** Which sidebar group this sits in (for grouping in the nav). */
    group: 'workspace' | 'learn' | 'connect' | 'account';
};

/**
 * Single map. Ordered top-to-bottom the way the sidebar renders
 * within each group.
 */
export const ROUTES: Record<TabKey, RouteDef> = {
    home:       { key: 'home',       label: 'Home',       icon: Home,           roles: 'any', group: 'workspace' },
    assistant:  { key: 'assistant',  label: 'Assistant',  icon: Bot,            roles: 'any', group: 'workspace' },

    education:  { key: 'education',  label: 'Education',  icon: BookOpen,       roles: 'any',                             access: 'education',  group: 'learn' },
    // Admins manage mentorship from Settings → Administration; the
    // Mentorship tab is scoped to mentor + mentee perspectives.
    mentorship: { key: 'mentorship', label: 'Mentorship', icon: UserCheck,      roles: ['mentee', 'mentor'],                                    group: 'learn' },

    networking:    { key: 'networking',    label: 'Networking', icon: Users,          roles: 'any',                             access: 'networking', group: 'connect' },
    conversations: { key: 'conversations', label: 'Messages',   icon: MessageSquare,  roles: 'any',                                                   group: 'connect' },

    // Administration panes were rolled into Settings (admin-only
    // groups). No dedicated Admin route.
    settings:   { key: 'settings',   label: 'Settings',   icon: Settings,       roles: 'any',                                                   group: 'account' },
    profile:    { key: 'profile',    label: 'Profile',    icon: User,           roles: 'any',                                                   group: 'account' },
};

// ── Role + access predicates ────────────────────────────────────────

// NAV_GROUPS used to live here but had no external consumer — the
// Sidebar composes its own grouping. Removed Phase K tranche 2.

function userHasRole(user: BackendUserWithPlan | null | undefined, role: Role): boolean {
    switch (role) {
        case 'admin': return isAdmin(user);
        case 'mentor': return isMentor(user);
        case 'mentee': return isMentee(user);
    }
}

export function canSeeRoute(
    route: RouteDef,
    user: BackendUserWithPlan | null | undefined,
): boolean {
    if (route.roles === 'any') return true;
    return route.roles.some((r) => userHasRole(user, r));
}

export function hasAccessForRoute(
    route: RouteDef,
    user: BackendUserWithPlan | null | undefined,
): boolean {
    if (!route.access) return true;
    // Admins bypass paywall gates.
    if (isAdmin(user)) return true;
    const flag = user?.access?.[route.access];
    return Boolean(flag);
}

/**
 * Role-aware label overrides. The Mentorship tab reads as "Coaching"
 * for mentees and "Review workspace" for mentors — the sub-views differ,
 * so the sidebar label should match the viewer's mental model. Admins
 * don't see the Mentorship tab (they manage from Settings).
 */
export function resolveRouteLabel(
    route: RouteDef,
    user: BackendUserWithPlan | null | undefined,
): string {
    if (route.key === 'mentorship') {
        if (isMentor(user)) return 'Review workspace';
        if (isMentee(user)) return 'Coaching';
    }
    return route.label;
}
